import { useState, useEffect, useCallback, useRef } from "react";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import FingerprintTable from "@/components/attendance/FingerprintTable";
import CaptureModal from "@/components/attendance/CaptureModal";
import VerifyModal from "@/components/attendance/VerifyModal";
import CandidateForm from "@/components/attendance/CandidateForm";
import MoveCandidateDialog from "@/components/attendance/MoveCandidateDialog";
import EnhanceDialog from "@/components/attendance/EnhanceDialog";
import Pagination from "@/components/attendance/Pagination";
import { api, formatApiError } from "@/lib/api";
import { useAtt } from "./AttendanceLayout";
import { PageTitle } from "./AttDashboard";

const LIMIT = 25;

export default function AttView() {
  const { me, isAdmin, centers, batches, reload: reloadShared } = useAtt();
  const [move, setMove] = useState(null);
  const [viewFp, setViewFp] = useState(null);
  const [capture, setCapture] = useState(null);
  const [verify, setVerify] = useState(null);
  const [form, setForm] = useState(null);
  const [batch, setBatch] = useState("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const ctrl = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => setSearch(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [search, batch]);

  const load = useCallback(async (silent = false) => {
    const my = ++seq.current;
    ctrl.current?.abort();
    const ac = new AbortController(); ctrl.current = ac;
    if (!silent) setState("loading");
    try {
      const r = await api.get("/attendance/candidates/page", { params: { page, limit: LIMIT, search: search || undefined, batch_id: batch !== "all" ? batch : undefined }, signal: ac.signal });
      if (my !== seq.current) return;
      setData(r.data); setError(null); setState("loaded");
      if (r.data.page !== page) setPage(r.data.page); // server clamped an out-of-range page
    } catch (e) {
      if (ac.signal.aborted || my !== seq.current) return;
      setError(formatApiError(e.response?.data?.detail) || e.message); setState("error");
    }
  }, [page, search, batch]);
  useEffect(() => { load(); }, [load]);

  const reload = () => { load(true); reloadShared(); };
  const changePage = (p) => { setPage(p); tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const list = (data?.items || []).map((c) => ({ ...c, batch_name: batches.find((b) => b.id === c.batch_id)?.name }));

  return (
    <div>
      <PageTitle title="Candidates" crumb="Candidates › View" />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input data-testid="view-search" placeholder="Search name or candidate ID…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:w-72 bg-white" />
        <Select value={batch} onValueChange={setBatch}><SelectTrigger className="sm:w-64 bg-white" data-testid="view-batch-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All batches</SelectItem>{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
        <p className="text-sm text-slate-500 self-center flex items-center gap-2" data-testid="view-count">{data ? `${data.total_count} candidate(s)` : "…"}{state === "loading" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}</p>
      </div>
      <div ref={tableRef} className="scroll-mt-4">
        {state === "error" ? (
          <div data-testid="candidates-error" className="bg-white rounded-2xl border border-slate-200 py-12 flex flex-col items-center gap-3 text-sm text-red-700"><AlertTriangle className="w-6 h-6" /> Could not load candidates: {error}<Button size="sm" variant="outline" data-testid="candidates-retry" onClick={() => load()} className="rounded-lg"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry</Button></div>
        ) : (
          <div className={state === "loading" ? "opacity-60 transition-opacity" : "transition-opacity"} aria-busy={state === "loading"}>
            <FingerprintTable candidates={list} reload={reload} loadState={state} emptyText={search || batch !== "all" ? "No candidates found." : undefined} onCapture={(c, n, existing) => setCapture({ c, n, existing })} onVerify={(c) => setVerify(c)} onEdit={(c) => setForm({ candidate: c })} onMove={(c) => setMove(c)} onViewFinger={(c, fp) => setViewFp({ c, fp })} isAdmin={isAdmin}
              footer={data && <Pagination page={data.page} pages={data.total_pages} total={data.total_count} limit={data.limit} onChange={changePage} busy={state === "loading"} />} />
          </div>
        )}
      </div>
      {move && <MoveCandidateDialog candidate={move} batches={batches} isAdmin={isAdmin} onClose={() => setMove(null)} onMoved={reload} />}
      {viewFp && <EnhanceDialog fp={viewFp.fp} candidate={viewFp.c} onClose={() => setViewFp(null)} />}
      <CaptureModal open={!!capture} onClose={() => setCapture(null)} candidate={capture?.c} finger={capture?.n} existing={capture?.existing} onSaved={reload} />
      <VerifyModal open={!!verify} onClose={() => setVerify(null)} candidate={verify} />
      <CandidateForm open={!!form} onClose={() => setForm(null)} candidate={form?.candidate} centers={centers} batches={batches} me={me} onSaved={reload} />
    </div>
  );
}
